import { useCallback } from 'react'
import { toast } from '../services/toastService'
import { locationAPI } from '../services/api'
import { applyMarkerOffsets } from '../pages/map/utils'
import { createLogger } from '../utils/logger'

const logger = createLogger('useEntityFetching')

/**
 * Custom hook for fetching nearby entities with sighting history
 * Handles data transformation, marker offsetting, and request cancellation
 * 
 * @param {Function} t - Translation function for error messages
 * @param {number} radius - Search radius in kilometers
 * @param {Function} setIsLoading - Loading state setter
 * @param {Function} setEntities - Entities state setter
 * @returns {Object} Object containing fetchNearbyEntities function
 */
export function useEntityFetching(t, radius, setIsLoading, setEntities) {
  const fetchNearbyEntities = useCallback(async (
    location,
    searchRadius = null,
    forceFresh = false,
    signal = null
  ) => {
    setIsLoading(true)
    const radiusToUse = searchRadius !== null ? searchRadius : radius
    
    try {
      // Use the new endpoint that returns recent sightings for each entity
      const params = {
        latitude: location.latitude,
        longitude: location.longitude,
        radius: radiusToUse * 1000, // Convert to meters
        historyLimit: 1 // Show only the latest sighting per entity on homepage
      }
      
      // Add cache-buster when explicitly forcing fresh data (e.g., after spot submission)
      if (forceFresh) {
        params._t = Date.now()
      }
      
      // Configure axios to bypass cache when forceFresh is true
      const config = forceFresh ? {
        params,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        signal
      } : { params, signal }
      
      const response = await locationAPI.getNearbyEntitiesWithHistory(config.params, config.headers, signal)

      // Transform the nested structure into flat array of sighting markers
      const allSightings = []
      const entitiesWithSightings = response.data.entities || []

      entitiesWithSightings.forEach((entityWithSightings) => {
        const entity = entityWithSightings.entity
        const sightings = entityWithSightings.sightings || []
        const totalCount = entityWithSightings.totalCount || sightings.length
        const hasMore = entityWithSightings.hasMore || false

        sightings.forEach((sighting, sightingIndex) => {
          // Calculate opacity based on recency (latest = 1.0, older = decreasing)
          const isLatest = sightingIndex === 0
          const opacity = isLatest ? 1.0 : Math.max(0.4, 1.0 - (sightingIndex * 0.15))
          
          // Check if this is a fallback unconfirmed sighting (latest but no confirmations)
          // This happens when showing most recent sighting because no confirmed ones exist in 72h window
          const confirmationCount = sighting.confirmationCount || 0
          const isFallbackUnconfirmed = isLatest && confirmationCount === 0 && totalCount > 1

          const markerData = {
            id: sighting.id,
            userId: sighting.userId || sighting.user_id,
            entityId: entity.id,
            name: entity.name,
            description: sighting.notes || 'Sighting location',
            currentLatitude: sighting.latitude,
            currentLongitude: sighting.longitude,
            currentAddress: sighting.address,
            confirmationCount: confirmationCount,
            lastSeenAt: sighting.createdAt,
            imageUrl: entity.imageUrl || entity.image_url,
            category: entity.category,
            type: entity.type,
            isSighting: true,
            isLatest: isLatest,
            isFallbackUnconfirmed: isFallbackUnconfirmed,
            markerOpacity: opacity,
            sightingIndex: sightingIndex,
            totalSightings: totalCount,
            hasMoreSightings: hasMore
          }
          
          // Log marker data for debugging
          if (!markerData.entityId) {
            logger.warn('Marker created without entityId', { markerData })
          }
          
          allSightings.push(markerData)
        })
      })

      // Apply slight offset to overlapping markers so they're all visible and clickable
      const offsetSightings = applyMarkerOffsets(allSightings)
      setEntities(offsetSightings)
      return offsetSightings // Return the entities array so callers can check if there are results
    } catch (error) {
      // Don't log or show error for aborted requests
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        logger.info('Fetch request was cancelled')
        return []
      }
      
      logger.error('Failed to fetch nearby entities', { error: error.message })
      toast.error(t('map.failedToLoadNearby'))
      return [] // Return empty array on error
    } finally {
      setIsLoading(false)
    }
  }, [radius, t, setIsLoading, setEntities])

  return { fetchNearbyEntities }
}
